import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ToolCallDetailSheet from './ToolCallDetailSheet';
import Icon from './icons/Icon';
import { describeTool } from '../utils/toolTitles';
import './WorkflowTimeline.css';

// Tool names never reach the user: each call reads as a sentence ("Editing file App.jsx"), from the catalog the
// vfs-service MCP server publishes (see utils/toolTitles.js). A tool it does not know is humanized, so a future tool
// never disappears from the timeline.
function describeToolCall(toolCall) {
    return describeTool(toolCall.name, toolCall.args);
}

function toolCallStatus(toolCall) {
    if (!toolCall.endedAt) { return 'running'; }
    const response = toolCall.response;
    const failed = response && typeof response === 'object' &&
        (response.success === false || response.error || response.isError === true);
    return failed ? 'error' : 'done';
}

// Groups tool calls by the turn they were stamped with (VfsChatPanel's
// turnIndexRef, see handleToolCall/handleTurnComplete) — the finest
// grouping granularity actually available (no signal exists for multiple
// sub-groups within one turn, since tool calls arrive via a side callback
// with no ordering relative to the assistant's own text). Calls with no
// turnIndex (pre-existing sessions from before this field existed) all fall
// into one group, keyed -1.
function groupByTurn(toolCalls) {
    const groups = [];
    const indexByTurn = new Map();
    for (const toolCall of toolCalls) {
        const turnIndex = toolCall.turnIndex ?? -1;
        let group = indexByTurn.get(turnIndex);
        if (!group) {
            group = { turnIndex, toolCalls: [] };
            indexByTurn.set(turnIndex, group);
            groups.push(group);
        }
        group.toolCalls.push(toolCall);
    }
    return groups;
}

const KIND_LABELS = {
    write: (n) => `Wrote ${n} file${n === 1 ? '' : 's'}`,
    read: (n) => `Read ${n} file${n === 1 ? '' : 's'}`,
    command: (n) => `Ran ${n} command${n === 1 ? '' : 's'}`,
    other: (n) => `${n} other step${n === 1 ? '' : 's'}`,
};
const KIND_ORDER = ['write', 'read', 'command', 'other'];

function summarizeGroup(group) {
    const counts = { write: 0, read: 0, command: 0, other: 0 };
    for (const toolCall of group.toolCalls) {
        counts[describeToolCall(toolCall).kind] += 1;
    }
    const parts = KIND_ORDER.filter((kind) => counts[kind] > 0).map((kind) => KIND_LABELS[kind](counts[kind]));
    return parts.length > 0 ? parts.join(', ') : `${group.toolCalls.length} step${group.toolCalls.length === 1 ? '' : 's'}`;
}

function groupStatus(group) {
    const statuses = group.toolCalls.map(toolCallStatus);
    if (statuses.includes('running')) { return 'running'; }
    if (statuses.includes('error')) { return 'error'; }
    return 'done';
}

/**
 * The toggle button alone — split out from the panel below so a host can
 * place it in a REAL layout slot (e.g. mia-chatbot-widget's own
 * `header.actions`) instead of the panel's absolutely-positioned
 * `top: 76px` hack, which assumed the widget's internal header always
 * renders at exactly that height. Real, reproducible bug this fixes: a new
 * sibling element added above the chat panel in one host (Home's embedded
 * "Creating your project…" status bar) didn't even need to change this
 * button's true math (it's positioned relative to `.chat-container`, not
 * page-level siblings) — but the underlying hardcoded-pixel approach was
 * ALREADY fragile the moment the widget's own header height varied even a
 * little (a longer conversation title wrapping, the playbook picker's
 * width changing), floating this toggle on top of live chat content
 * instead of staying clear of it. Rendering it as real header content
 * removes the guesswork entirely — its position is however the header
 * actually lays out, not a number someone had to keep re-measuring by eye.
 */
export function WorkflowTimelineToggle({ toolCalls, isOpen, onToggle }) {
    const runningCount = useMemo(
        () => toolCalls.filter((tc) => toolCallStatus(tc) === 'running').length,
        [toolCalls],
    );

    if (toolCalls.length === 0) {
        return null;
    }

    return (
        <button
            type="button"
            className={`workflow-timeline-toggle${runningCount > 0 ? ' is-active' : ''}`}
            onClick={onToggle}
            title={isOpen ? 'Hide workflow steps' : 'Show workflow steps'}
        >
            {runningCount > 0 ? <Icon name="spinner" size={16} /> : '⚙'}
            {runningCount > 0 && <span className="workflow-timeline-badge">{runningCount}</span>}
        </button>
    );
}

WorkflowTimelineToggle.propTypes = {
    toolCalls: PropTypes.arrayOf(PropTypes.object).isRequired,
    isOpen: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

/**
 * The expand panel (grouped tool-call list + drill-down) — see
 * `WorkflowTimelineToggle` above for why the toggle button itself moved
 * out of this component. The panel keeps its own `position: absolute`
 * (WorkflowTimeline.css) since it only renders while open (a user-
 * triggered, transient state), unlike the toggle, which was permanently
 * visible and therefore permanently at risk of drifting out of place.
 */
export default function WorkflowTimeline({ toolCalls, isOpen, onToggle, vfsProjectId }) {
    const listRef = useRef(null);
    const [openGroupIndex, setOpenGroupIndex] = useState(null);
    const groups = useMemo(() => groupByTurn(toolCalls), [toolCalls]);

    useEffect(() => {
        if (isOpen && listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [toolCalls, isOpen]);

    if (toolCalls.length === 0 || !isOpen) {
        return null;
    }

    const openGroup = openGroupIndex !== null ? groups[openGroupIndex] : null;

    return (
        <>
            <div className="workflow-timeline-panel">
                <div className="workflow-timeline-header">
                    <span>Workflow steps</span>
                    <button type="button" className="workflow-timeline-close" onClick={onToggle}>&times;</button>
                </div>
                <div className="workflow-timeline-list" ref={listRef}>
                    {groups.map((group, groupIndex) => {
                        const status = groupStatus(group);
                        const duration = group.toolCalls.reduce((total, tc) => (
                            tc.startedAt && tc.endedAt ? total + (tc.endedAt - tc.startedAt) : total
                        ), 0);
                        return (
                            <button
                                key={group.turnIndex}
                                type="button"
                                className={`workflow-timeline-step is-${status}`}
                                onClick={() => setOpenGroupIndex(groupIndex)}
                            >
                                <span className="workflow-timeline-dot" />
                                <div className="workflow-timeline-step-body">
                                    <div className="workflow-timeline-step-title">
                                        {summarizeGroup(group)}
                                        {duration > 0 && (
                                            <span className="workflow-timeline-duration">
                                                {duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(1)}s`}
                                            </span>
                                        )}
                                    </div>
                                    {status === 'error' && (
                                        <div className="workflow-timeline-step-error">One or more steps failed</div>
                                    )}
                                </div>
                                <span className="workflow-timeline-step-chevron">›</span>
                            </button>
                        );
                    })}
                </div>
            </div>
            {openGroup && (
                <ToolCallDetailSheet
                    group={openGroup}
                    onClose={() => setOpenGroupIndex(null)}
                    vfsProjectId={vfsProjectId}
                />
            )}
        </>
    );
}

WorkflowTimeline.propTypes = {
    toolCalls: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        args: PropTypes.object,
        response: PropTypes.any,
        startedAt: PropTypes.number,
        endedAt: PropTypes.number,
        turnIndex: PropTypes.number,
    })).isRequired,
    isOpen: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    // Needed to fetch a real diff for a file-edit entry (vfsClient.
    // getCommitDiff) — undefined is fine, ToolCallDetailSheet just won't
    // offer the diff drill-down without it.
    vfsProjectId: PropTypes.string,
};

export { describeToolCall, toolCallStatus };
